<template>
  <div>
    <transition name="fade" mode="out-in">
      <div v-if="!hiredOrNot">
        <hired-not-hired
            @hired-nothired="onHiredOrNot"
            :data="{firstName, profileName, profileLogo, subtaskName, workOrder}"/>
      </div>
      <div v-else>
        <review-confirmation
            v-if="isSubmitted"
            :order_hash="workOrder.order_hash"
            :reviewedCount="reviewedCount"/>
        <error-review @retry-review="onReviewSubmit" v-else-if="isError"/>
        <review-preview
            v-else-if="showPreview"
            :data="previewData"
            @hide-preview="onHidePreview"
            @submit-preview="onReviewSubmit"/>

        <form v-else @submit.prevent="onReviewSubmit">
          <div class="d-flex fd-column ai-center">
            <div
                class="h2 text-bold text-center m-y-4"
                selenium="review-main-header">Review a Pro
            </div>
            <pro-avatar :image="profileLogo" :size="$device.isMobile ? 64 : 96" class="m-b-2" />
            <p class="text-center text-md m-b-2 c-gray70" selenium="review-header-hint">
              <span class="ASTERISK"></span>
              All fields are required unless marked as optional.
            </p>
            <div class="d-flex fd-column ai-center">
              <p class="SUB-HEADING p-t-4:mo p-b-2" selenium="review-secondary-header">
                <span class="ASTERISK"></span>
                How was your overall experience with {{ profileName }}?
              </p>
              <div
                  class="OVERALL-RATING input-container m-b-4"
                  :class="{'error': showError && overallRating === 0}"
                  error="This field is required">
                <div class="d-flex p-x-6">
                  <div
                      v-for="(label, index) in $options.REVIEW_LABELS"
                      :key="index"
                      :class="['STAR', 'flex-1-1', 'm-0', {
                        'PRESELECTED': index + 1 < overallRating,
                        'SELECTED': index + 1 === overallRating,
                      }]"
                      @click="onRatingClick((index + 1), 'overall')">
                    <img class="STAR-ICON">
                    <span
                        class="text-sm"
                        :selenium="'review-overall-label-' + label">{{ label }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="d-flex fd-column ai-center p-b-2">
              <div class="SUB-HEADING" selenium="review-categories-header">
                <span class="ASTERISK"></span>
                How would you rate {{ profileName }} in the following categories?
              </div>
              <div
                  v-for="(v,k,catIndex) in categories"
                  :key="catIndex">
                <div
                    v-if="v.required"
                    class="text-center text-bold c-gray70"
                    :selenium="'review-category-' + v.label">
                  <span class="ASTERISK"></span> {{v.label}}

                </div>
                <div
                    :class="['CATEGORY-RATING', 'input-container', k,
                             {'error': showError && v.rating === 0 && v.required }]"
                    error="This field is required">
                  <div v-if="v.required" class="d-flex">
                    <div
                        v-for="(label, index) in $options.REVIEW_LABELS"
                        :key="index"
                        :class="['STAR', 'm-0', {
                          'PRESELECTED': index + 1 < v.rating,
                          'SELECTED': index + 1 === v.rating,
                        }]"
                        @click="onRatingClick((index + 1), k)">
                      <img class="STAR-ICON">
                    </div>
                  </div>
                </div>
              </div>
            </div>


            <div class="layout-item is-12 fd-column ai-center is-6:t m-b-2">
              <div class="SUB-HEADING text-left p-l-0" selenium="review-text-header">
                <span class="ASTERISK"></span> Tell us about your experience.
              </div>
              <div
                  class="m-b-2 m-x-1:mo GENERAL-RATING input-container"
                  :class="{'error': showError && reviewComments === ''}"
                  error="This field is required">
                <textarea
                    v-auto-expand
                    v-model.trim="reviewComments"
                    class="input bg-white"
                    name="review"
                    rows="6"
                    placeholder="My experience working with this Pro was…"
                    selenium="review-text-box"
                />
              </div>

              <div class="m-t-3 d-flex fd-column p-l-1:mo">
                <label class="m-b-1 text-md">(Optional)</label>
                <div class="text-semibold">
                  <label
                      :class="{ 'cur-not-allowed': uploadInProgress }"
                      class="btn-secondary m-b-1"
                      selenium="review-add-photos">
                    <img src="./assets/Camera.svg" class="m-r-1 c-depot" size="15" />
                    <input
                        :disabled="uploadInProgress"
                        class="d-none"
                        type="file"
                        multiple
                        accept="image/*"
                        @change="uploadImage"
                        selenium="review-add-photos">
                    Add Photos
                  </label>
                  <photo-info/>
                  <special-message
                      ref="uploadPhotoErrorMessage"
                      type="error"
                      class="PHOTO-UPLOAD-ERROR">
                    {{ uploadPhotoError ||
                    'Failed to upload some photos. Please try again later.' }}
                  </special-message>
                </div>

                <div class="d-flex flex-wrap m-t-2">
                  <div v-if="loadingFile" class="LOADING-FILE">
                    <spinner/>
                  </div>
                  <gallery
                      v-if="photos.length"
                      :files="photos"
                      deletable
                      @deleteImage="deleteImage"/>
                </div>
              </div>
            </div>

            <div class="layout-item is-12 fd-column ai-center is-6:t">
              <div class="checkboxharmony p-r-3 p-l-1:mo">
                <input
                    v-model="isAnonymous"
                    id="ananomous"
                    type="checkbox"
                    selenium="review-anonymous-check">
                <label for="ananomous" class="text-md">
                  <p>Anonymous review (do not post my name)
                  </p>
                </label>
              </div>
            </div>

            <div class="BUTTONS layout-item is-12 is-6:t">
              <button
                  class="btn-secondary btn-sm btn-lg:t text-semibold"
                  :class="{ 'x-pulse': isCreating }"
                  @click.prevent="onPreview"
                  selenium="submit-preview">
                Preview
              </button>
              <button
                  type="submit"
                  class="btn btn-sm btn-lg:t text-semibold"
                  :class="{ 'x-pulse': isCreating }"
                  :disabled="isCreating || isDisabled"
                  selenium="submit-review"
                  v-tooltip="{
                    content:isDisabled
                      ? 'Please ensure all required fields are entered before submitting.'
                      : '',
              }">
                Submit Review
              </button>
            </div>
          </div>
        </form>
        <image-popup
            :isOpen="isShowImagePopup"
            @close= "isShowImagePopup=false"/>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import { submitReview, getReview } from '@homeservices/api-kit/workorder/reviews/';
import { uploadAsset, deleteAsset } from '@homeservices/api-kit/asset';
import _get from 'lodash/get';
import http from '@homeservices/api-kit/axios';
import reviewParams from '@/mixins/review-params';
import reviews from '@/mixins/reviews';
import publicProfile from '@/mixins/public-profile';
import workOrder from '@/mixins/work-order';
import ProAvatar from '@/components/ProAvatar';
import SpecialMessage from '@/components/SpecialMessage';
import Gallery from '@/components/Gallery/';
import {
  VALID_IMAGE_TYPES,
  VALID_IMAGE_ACCEPT_TYPES,
  DEFAULT_MAX_IMAGE_SIZE,
} from '@/constants';
import PhotoInfo from '@/components/PhotoInfo';
import ImagePopup from '@/components/ServicesPricing/ImagePopup';
import ReviewPreview from './ReviewPreview';
import ReviewConfirmation from './ReviewConfirmation';
import ErrorReview from './ErrorReview';
import HiredNotHired from './HiredNotHired';


export default {
  VALID_IMAGE_TYPES,
  VALID_IMAGE_ACCEPT_TYPES,
  DEFAULT_MAX_IMAGE_SIZE,
  name: 'create-review',
  components: {
    ProAvatar,
    ReviewConfirmation,
    ErrorReview,
    ReviewPreview,
    SpecialMessage,
    Gallery,
    HiredNotHired,
    PhotoInfo,
    ImagePopup,
  },
  mixins: [
    reviewParams,
    reviews,
    publicProfile,
    workOrder,
  ],
  data () {
    return {
      reviewedCount: 0,
      hiredOrNot: false,
      isSubmitted: false,
      showPreview: false,
      showError: false,
      isCreating: false,
      uploadInProgress: false,
      uploadPhotoError: '',
      loadingFile: false,
      overallRating: 0,
      reviewComments: '',
      isAnonymous: false,
      isError: false,
      isShowImagePopup: false,
      photos: [],
      categories: {
        knowledge: {
          label: 'Professionalism',
          rating: 0,
          required: true,
        },
        responsiveness: {
          label: 'Communication',
          rating: 0,
          required: true,
        },
        quality_of_work: {
          label: 'Quality of work',
          rating: 0,
          required: true,
        },
      },
    };
  },
  computed: {
    ...mapGetters('user', [
      'firstName',
      'lastName',
    ]),
    profile () {
      return this.getPublicProfileByServiceProviderIdentifier(this.profileId);
    },
    profileName () {
      return this.profile && this.profile.service_provider.business_name;
    },
    subtaskName () {
      return this.workOrder && this.workOrder.service_display_name;
    },
    profileLogo () {
      return this.profile && this.profile.service_provider.logo;
    },
    isDisabled () {
      return (
        this.overallRating === 0
        || this.reviewComments === ''
        || this.categories.knowledge.rating === 0
        || this.categories.responsiveness.rating === 0
        || (this.categories.quality_of_work.required === true
           && this.categories.quality_of_work.rating === 0)
      );
    },
    previewName () {
      return this.isAnonymous
        ? 'Proreferral Customer'
        : `${this.workOrder.user.first_name} ${this.workOrder.user.last_name}`;
    },
    previewData () {
      let preview = {
        ...this.requestBody,
        categories: this.requestBody.category_rating,
        profileName: this.profileName,
        userFullName: this.previewName,
        profileLogo: this.profileLogo,
      };
      preview = Object.assign({}, preview);
      preview.assets = this.photos;
      return preview;
    },
    requestBody () {
      return {
        order_hash: this.workOrder.order_hash,
        service_provider_id: this.profile.service_provider.id,
        is_hired: this.categories.quality_of_work.rating > 0,
        is_anonymous: this.isAnonymous,
        overall_rating: this.overallRating,
        review_comments: this.reviewComments,
        assets: this.photos.map((x, i) => ({
          type: 'picture',
          name: `review_${this.workOrder.user_friendly_identifier}_${i}`,
          path: x.url,
          thumbnail_path: x.thumbnail,
        })),
        category_rating: Object.keys(this.categories).reduce((result, key) => {
          const r = result;
          r[key] = this.categories[key].rating;
          return r;
        }, {}),
      };
    },
  },
  async mounted () {
    this.overallRating = this.rating || this.overallRating;
    try {
      await this.fetchWorkOrder({ jobId: this.jobId, includePublicProfiles: true });
    } catch (error) {
      this.close();
      console.error(error); // eslint-disable-line no-console
    }
  },
  methods: {
    onHiredOrNot (evt) {
      this.hiredOrNot = evt;
      if (evt === 'no') {
        this.categories.quality_of_work.required = false;
      }
    },
    onRatingClick (rating, type) {
      if (type === 'overall') {
        this.overallRating = rating;
      } else {
        this.categories[type].rating = rating;
      }
    },
    onPreview () {
      this.showPreview = true;
    },
    onHidePreview () {
      this.showPreview = false;
    },
    async onReviewSubmit () {
      this.showError = false;
      this.showPreview = false;
      this.isSubmitted = false;
      this.isError = false;

      if (this.isDisabled) {
        this.showError = true;
        return;
      }

      try {
        this.isCreating = true;
        const { data } = await getReview({ order_hashes: this.workOrder.order_hash, from: 0 });
        this.reviewedCount = data.count;
        await submitReview(this.requestBody);
        this.fireMixpanel();
        this.reviewedCount += 1;
        setTimeout(() => {
          this.isSubmitted = true;
        }, 500);
      } catch (error) {
        this.isError = true;
      } finally {
        this.isCreating = false;
      }
    },
    async uploadImage (event) {
      this.$refs.uploadPhotoErrorMessage.hide();

      const files = Object.values(_get(event, 'target.files')) || Object.values(_get(event, 'dataTransfer.files'));
      if (!files.length) {
        return;
      }
      if (files.length <= 8) {
        files.forEach(async (file) => {
          if (this.imageHasError(file)) {
            return this.$refs.uploadPhotoErrorMessage.trigger(5000);
          }

          this.loadingFile = true;

          try {
            const formData = new FormData();
            formData.append('file', file);
            const { data } = await uploadAsset('service-providers-reviews', this.profile.service_provider.id, formData);
            const { data: { results: [result] } } = await http.get(`/platform/coreassets/r/assets/images/v1?url=${data.url}`);
            data.id = result.id;
            this.photos.push(data);
          } catch (error) {
            console.error(error);
          }
        });
      } else {
        this.isShowImagePopup = true;
      }
      this.loadingFile = false;
    },
    async deleteImage (id) {
      try {
        await deleteAsset('service-providers-reviews', id);
        this.photos.splice(this.photos.findIndex(photo => photo.id === id), 1);
      } catch (error) {
        console.error(error);
      }
    },
    imageHasError (img) {
      if (this.$options.VALID_IMAGE_TYPES.indexOf(img.type) < 0) {
        this.uploadPhotoError = 'File type not accepted.';
        return true;
      } if (img.size > this.$options.DEFAULT_MAX_IMAGE_SIZE) {
        this.uploadPhotoError = 'Image should not exceed 5 MB.';
        return true;
      }
      this.uploadPhotoError = '';
      return false;
    },
    fireMixpanel () {
      const { id, occupation_id, occupation_name, service_provider } = this.profile;
      const category_rating = Object.values(this.categories).reduce((acc, cur) => {
        acc[cur.label.split(' (Optional)')[0]] = cur.rating;
        return acc;
      }, {});

      const params = {
        overall_rating: this.requestBody.overall_rating,
        skills_profile: id,
        service_provider_id: service_provider.id,
        occupation_id,
        occupation_name,
        lead_status: this.workOrder.status,
        ...category_rating,
        review_details: this.requestBody.review_comments,
        review_length: this.requestBody.review_comments.length,
        number_of_pictures: this.requestBody.assets.length,
      };

      if (this.requestBody.is_hired) {
        this.$events.$emit(this.$events.CONSUMER_HIRED_FEEDBACK, params);
      } else {
        this.$events.$emit(this.$events.CONSUMER_NOT_HIRED_FEEDBACK, params);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "~@homeservices/design-kit/tools";

  $transition-half-duration: 0.15s;

  .HEADING {
    padding: 2*$unit 4*$unit 0;
    font-weight: 600;
    text-align: center;
    max-width: 700px;
    @include tablet {
      @include h4;
    }
  }

  .SUB-HEADING {
    font-size: 16px;
    font-weight: 600;
    text-align: left;

    @include tablet {
      @include h6;
      text-align: center;
    }
  }

  .STAR {
    @include text-small;
    @include margin(0 0.5, 0 3);
    display: flex;
    flex-direction: column;
    width: 55px;
    align-items: center;
    color: $color-gray70;
    transition: font-weight $transition-half-duration;


    &:hover,
    &.SELECTED {
      font-weight: 700;

    }

    &:hover,
    &.PRESELECTED,
    &.SELECTED  {
      .STAR-ICON {
        color: $color-depot;
        content: url('./assets/StarSelected.png')

      }
    }

    .STAR-ICON {
      @include h4;
      margin-bottom: $unit;
      transition: color $transition-half-duration;
      content: url('./assets/Star.png')
    }
    .STAR-TOP {
      position: absolute;
      left:0;

      color:red;
      z-index: 100;

    }
    @include tablet {
      @include h6;
    }

  }

  .LOADING-FILE {
    @include unit(width height, 6, 10);
    @include margin-right(1);
    display: flex;
    align-items: center;
    justify-content: center;
    color: $color-gray80;
  }

  .HIRED {
    @include text-small;
    margin-bottom: 2*$unit;

    @include tablet {
      @include h6;

      input + label:before {
        @include square(25px);
      }
    }
  }
  .BUTTONS {
    display: flex;
    justify-content: end;
    margin: 2*$unit 2*$unit 4*$unit;
    button {
      margin: $unit;
    }
    @include tablet {
      margin: 2*$unit auto 4*$unit;
      max-width: 700px;
    }
  }
  .OVERALL-RATING {
    max-width: 360px;
  }
  .GENERAL-RATING, .OVERALL-RATING, .CATEGORY-RATING  {
    &::after {
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      font-weight: 600;
      font-size: 12px;
    }
  }
  .ASTERISK:before {
    content: "*";
    font-size: 45px;
    font-weight: 400;
    position: relative;
    top: $unit * 2.25;
    color: #F5222D;
  }
  textarea::placeholder {
    line-height:1.5;
  };
</style>
